import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import './Login.css';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { FaEye, FaEyeSlash } from 'react-icons/fa';
import { ClipLoader } from 'react-spinners';
import Cookies from 'js-cookie';
import API_BASE_URL from '../../config/api';

const Login = () => {
    const navigate = useNavigate();
    const [formData, setFormData] = useState({
        email: '',
        password: ''
    });
    const [loading, setLoading] = useState(false);
    const [forgotPasswordOpen, setForgotPasswordOpen] = useState(false);
    const [resetEmail, setResetEmail] = useState('');
    const [resetLoading, setResetLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);

    useEffect(() => {
        const token = Cookies.get('token');
        const userRole = Cookies.get('userRole');

        if (token && userRole) {
            switch (userRole) {
                case 'overall_manager':
                    navigate('/admin/dashboard');
                    break;
                case 'admin_manager':
                    navigate('/hr-manager/dashboard');
                    break;
                case 'immediate_manager':
                case 'md':
                    navigate('/manager/dashboard');
                    break;
                case 'staff':
                    navigate('/employee/dashboard');
                    break;
                default:
                    break;
            }
        }
    }, [navigate]);

    const handleChange = (e) => {
        setFormData({
            ...formData,
            [e.target.name]: e.target.value
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);

        try {
            const response = await axios.post(`${API_BASE_URL}/api/v1/users/login/`, formData);
            const data = response.data;

            // 2FA required: login returns a challenge_token instead of access tokens
            const challengeToken =
                data.challenge_token ||
                data.challengeToken ||
                data.totp_challenge_token;

            if (
                challengeToken ||
                data.two_factor_required ||
                data.requires_2fa ||
                data.mfa_required
            ) {
                if (!challengeToken) {
                    Swal.fire({
                        icon: 'error',
                        title: 'Error',
                        text: 'Two-factor authentication is required, but no challenge token was returned.',
                    });
                    return;
                }

                sessionStorage.setItem('challenge_token', challengeToken);
                sessionStorage.setItem('2fa_email', formData.email);

                Swal.fire({
                    icon: 'info',
                    title: 'Verification required',
                    text: 'We sent an OTP to your email. Please enter it to continue.',
                    timer: 2000,
                    showConfirmButton: false,
                });

                navigate('/verify-2factor', {
                    state: {
                        challenge_token: challengeToken,
                        email: formData.email,
                    },
                });
                return;
            }

            const roleCodename = typeof data.user.role === 'object' ? data.user.role?.codename : data.user.role;

            Cookies.set('token', data.access, { expires: 7, path: '/' });
            Cookies.set('userRole', roleCodename);
            Cookies.set('employeeId', data.user.id);
            Cookies.set('currency', data.company?.currency);
            Cookies.set('subscribedModules', JSON.stringify(data.company?.subscribed_modules || []), { expires: 7, path: '/' });
            Cookies.set('user', JSON.stringify(data.user), { expires: 7, path: '/' });
            Cookies.set('userPermissions', JSON.stringify(data.user?.permissions || []), { expires: 7, path: '/' });
            Cookies.set('company_name', data.company?.trade_name);

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Login successful',
                timer: 1500
            });

            if (data.user.first_login) {
                switch (roleCodename) {
                    case 'hr_manager':
                        navigate('/hr/change-password', {
                            state: { oldPassword: data.user.employee_id }
                        });
                        break;
                    case 'immediate_manager':
                        navigate('/manager/change-password', {
                            state: { oldPassword: data.user.employee_id }
                        });
                        break;
                    case 'staff':
                        navigate('/employee/change-password', {
                            state: { oldPassword: data.user.employee_id }
                        });
                        break;
                    default:
                        handleNormalLogin(roleCodename);
                        break;
                }
            } else {
                handleNormalLogin(roleCodename);
            }
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.detail || 'Login failed'
            });
        } finally {
            setLoading(false);
        }
    };

    const handleNormalLogin = (role) => {
        switch (role) {
            case 'overall_manager':
                navigate('/admin/dashboard');
                break;
            case 'hr_manager':
                navigate('/hr-manager/dashboard');
                break;
            case 'immediate_manager':
            case 'md':
                navigate('/manager/dashboard');
                break;
            case 'staff':
                navigate('/employee/dashboard');
                break;
            default:
                navigate('/');
                break;
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setResetLoading(true);

        try {
            await axios.post(`${API_BASE_URL}/api/v1/users/forgot-password/`, {
                email: resetEmail
            });

            Swal.fire({
                icon: 'success',
                title: 'Success!',
                text: 'Password reset instructions have been sent to your email',
                timer: 3000
            });

            setForgotPasswordOpen(false);
            setResetEmail('');
        } catch (error) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: error.response?.data?.message || 'Failed to process password reset request'
            });
        } finally {
            setResetLoading(false);
        }
    };

    return (
        <div className="login-page">
            <div className="login-bg-orb login-bg-orb--1" aria-hidden="true" />
            <div className="login-bg-orb login-bg-orb--2" aria-hidden="true" />
            <div className="login-bg-orb login-bg-orb--3" aria-hidden="true" />

            <div className="login-card">
                <div className="login-logo-wrap">
                    <div className="login-logo-backdrop">
                        <img
                            src="/assets/images/burray/burray.png"
                            alt="Burray HR"
                            className="login-logo"
                        />
                    </div>
                </div>

                <h1 className="login-title">Burray HR Software</h1>
                <p className="login-subtitle">Sign in to your account</p>

                <form onSubmit={handleSubmit}>
                    <div className="login-field">
                        <label htmlFor="email">Email</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            placeholder="Enter your email address"
                            className="login-input"
                            autoComplete="email"
                        />
                    </div>

                    <div className="login-field">
                        <label htmlFor="password">Password</label>
                        <div className="login-password-wrap">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                required
                                placeholder="Enter your password"
                                className="login-input"
                                autoComplete="current-password"
                            />
                            <button
                                type="button"
                                className="login-password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                            >
                                {showPassword ? <FaEyeSlash /> : <FaEye />}
                            </button>
                        </div>
                    </div>

                    <button
                        type="submit"
                        disabled={loading}
                        className="login-submit"
                    >
                        {loading ? (
                            <>
                                <ClipLoader size={18} color="#ffffff" />
                                Signing in…
                            </>
                        ) : (
                            'Sign In'
                        )}
                    </button>

                    <div className="login-forgot-wrap">
                        <button
                            type="button"
                            onClick={() => setForgotPasswordOpen(true)}
                            className="login-forgot-btn"
                        >
                            Forgot password?
                        </button>
                    </div>
                </form>

                <p className="login-footer">Powered by Burray Systems</p>
            </div>

            <Dialog
                open={forgotPasswordOpen}
                onClose={() => setForgotPasswordOpen(false)}
                maxWidth="sm"
                fullWidth
                className="login-dialog"
            >
                <DialogTitle className="login-dialog-title">Reset Password</DialogTitle>
                <DialogContent>
                    <p className="login-dialog-desc">
                        Enter your email address and we'll send you instructions to reset your password.
                    </p>
                    <div>
                        <label htmlFor="reset-email" style={{ display: 'block', marginBottom: '0.5rem', fontSize: '0.8125rem', fontWeight: 500, color: '#475569' }}>
                            Email
                        </label>
                        <input
                            type="email"
                            id="reset-email"
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            required
                            placeholder="Enter your email address"
                            className="login-dialog-input"
                        />
                    </div>
                </DialogContent>
                <DialogActions sx={{ padding: '1rem 1.5rem 1.5rem' }}>
                    <Button
                        onClick={() => setForgotPasswordOpen(false)}
                        className="login-dialog-cancel"
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleForgotPassword}
                        disabled={resetLoading || !resetEmail}
                        className="login-dialog-submit"
                    >
                        {resetLoading ? 'Sending…' : 'Send Reset Instructions'}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    );
};

export default Login;
